export interface FrameProfile {
    id: number;
    relIDs: number[];        // Relative IDs for frame position mapping
    name: string;            // Profile name (e.g., '080x05_080x20')
    image: string;           // Path to profile image
    wf: number;              // Frame width in meters
    uf: (number | null)[];           // Uf values [single-glaz, double, triple, quadruple]
}

export interface SpacerValues {
    spacerType: string;      // e.g., 'SwisspacerAdvance'
    psi: (number | null)[];           // Psi values [single, double, triple, quadruple]
    frsi: (number | null)[];       // fRsi values [single, double, triple, quadruple]
}

export interface ProfileSystem {
    id: number;
    name: string;            // System name (e.g., 'energeto 8000 foam inside')
    image: string;           // System representative image
    frameType: string;       // 'FRAME_TYPE_PVC' | 'FRAME_TYPE_WOOD' | 'FRAME_TYPE_ALU_WOOD' | 'FRAME_TYPE_ALU_METAL'
    variant?: string;        // System variant (optional)
    profiles: FrameProfile[];
    spacerValues?: SpacerValues[];  // Optional spacer performance data
}

export interface ProfileWindowsMapping {
    wid: number;             // Window type ID
    f0: number[];            // Frame position 0 - relIDs
    f1: number[];            // Frame position 1
    f2: number[];            // Frame position 2
    f3: number[];            // Frame position 3
    f4: number[];            // Frame position 4
    f5: number[];            // Frame position 5
    f6: number[];            // Frame position 6
}

export interface SystemManufacturer {
    id: number;
    name: string;
    image: string;           // Manufacturer logo
    windowsMap: ProfileWindowsMapping[];
    systems: ProfileSystem[];
}

// ============================================
// CONSTANTS
// ============================================

export const FRAME_TYPES = {
    WOOD: 'FRAME_TYPE_WOOD',
    PVC: 'FRAME_TYPE_PVC',
    ALU_WOOD: 'FRAME_TYPE_ALU_WOOD',
    ALU_METAL: 'FRAME_TYPE_ALU_METAL'
} as const;

export const SPACER_TYPES = {
    SWISSPACER_ADVANCE: 'SwisspacerAdvance',
    SWISSPACER_ULTIMATE: 'SwisspacerUltimate',
    SWISSPACER_ULTIMATE_PRO: 'SwisspacerUltimatePro'
} as const;

// ============================================
// WINDOW TYPE IDs (for reference)
// ============================================
export const WINDOW_TYPES = {
    SINGLE_SASH: 1,
    DOUBLE_SASH: 2,
    TWO_PARTED_FIXED_TURN_TILT: 3,
    TRIPLE_SASH: 4,
    VERTICAL_SLIDING_SASH: 5,
    HORIZONTAL_SLIDING_SASH: 6,
    SINGLE_SASH_TOPLIGHT: 7,
    DOUBLE_SASH_TOPLIGHT: 8,
    FIXED_LIGHT: 9,
    CROSS_NONE: 10,
    DOUBLE_SASH_RIGHT_SPLIT: 11,
    TRIPLE_SASH_MIDDLE_SPLIT: 12,
    WER_FR_WINDOW: 13,
    WER_FR_DOOR: 14
} as const;

// ============================================
// MANUFACTURER DATA
// ============================================

export const FRAME_MANUFACTURERS: SystemManufacturer[] = [
    // ============================================
    // ALUPLAST (ID: 1)
    // ============================================
    {
        id: 1,
        name: 'Aluplast',
        image: 'assets/images/frames/aluplast/aluplast.png',
        windowsMap: [
            { wid: 1, f0: [2], f1: [2], f2: [2], f3: [2], f4: [], f5: [], f6: [] },
            { wid: 2, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
            { wid: 3, f0: [1], f1: [1], f2: [1], f3: [2], f4: [2], f5: [2], f6: [5] },
            { wid: 4, f0: [2], f1: [2], f2: [2], f3: [2], f4: [4], f5: [3, 4], f6: [] },
            { wid: 6, f0: [8], f1: [8], f2: [8], f3: [7], f4: [7], f5: [7], f6: [9] },
            { wid: 7, f0: [1], f1: [1], f2: [2], f3: [2], f4: [5, 6], f5: [], f6: [] },
            { wid: 8, f0: [1], f1: [1], f2: [2], f3: [5, 6], f4: [2], f5: [3, 4], f6: [] },
            { wid: 9, f0: [1], f1: [1], f2: [1], f3: [], f4: [], f5: [], f6: [] },
            { wid: 10, f0: [1], f1: [1], f2: [1], f3: [10], f4: [], f5: [], f6: [] },
            { wid: 11, f0: [1], f1: [1], f2: [1], f3: [10], f4: [10], f5: [], f6: [] },
            { wid: 12, f0: [1], f1: [1], f2: [1], f3: [1], f4: [10], f5: [10], f6: [10] },
            { wid: 13, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
            { wid: 14, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] }
        ],
        systems: [
            // System 1: energeto 8000 foam inside
            {
                id: 1,
                name: 'energeto 8000 foam inside',
                image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_02_080x05_080x20.png',
                frameType: FRAME_TYPES.PVC,
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: '080x05',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_01_080x05.png',
                        wf: 0.080,
                        uf: [0.86, 0.86, 0.80, 0.80]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: '080x05_080x20',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_02_080x05_080x20.png',
                        wf: 0.1185,
                        uf: [0.82, 0.82, 0.80, 0.80]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: '080x20_180x64_080x20',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_03_080x20_180x64_080x20.png',
                        wf: 0.142,
                        uf: [1.0, 1.0, 0.94, 0.94]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: '080x20_180x44_080x20',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_04_080x20_180x44_080x20.png',
                        wf: 0.182,
                        uf: [1.0, 1.0, 0.98, 0.98]
                    },
                    {
                        id: 5,
                        relIDs: [5],
                        name: '080x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_05_080x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 6,
                        relIDs: [6],
                        name: '080x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_06_080x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 7,
                        relIDs: [7],
                        name: '080x05_080x20',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_07_080x05_080x20.png',
                        wf: 0.1185,
                        uf: [0.82, 0.82, 0.80, 0.80]
                    },
                    {
                        id: 8,
                        relIDs: [8],
                        name: '080x05',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_08_080x05.png',
                        wf: 0.080,
                        uf: [0.86, 0.86, 0.80, 0.80]
                    },
                    {
                        id: 9,
                        relIDs: [9],
                        name: '080x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_09_080x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 10,
                        relIDs: [10],
                        name: '180x46_bonding inside',
                        image: 'assets/images/frames/aluplast/aluplast_energeto8000foaminside_10_180x46_bonding_inside.png',
                        wf: 0.070,
                        uf: [1.2, 1.2, 1.1, 1.1]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [0.0, 0.035, 0.035, 0.035],
                        frsi: [0.0, 0.66, 0.73, 0.73]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [0.0, 0.028, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.74, 0.74]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [0.0, 0.028, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.74, 0.74]
                    }
                ]
            },
            // System 2: IDEAL 8000
            {
                id: 2,
                name: 'IDEAL 8000',
                image: 'assets/images/frames/aluplast/aluplast_ideal8000_02_180x05_180x20.png',
                frameType: FRAME_TYPES.PVC,
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: '180x05',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_01_180x05.png',
                        wf: 0.080,
                        uf: [1.1, 1.1, 1.0, 1.0]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: '180x05_180x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_02_180x05_180x20.png',
                        wf: 0.1185,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: '180x20_180x64_180x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_03_180x20_180x64_180x20.png',
                        wf: 0.142,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: '180x20_180x44_180x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_04_180x20_180x44_180x20.png',
                        wf: 0.182,
                        uf: [1.2, 1.2, 1.2, 1.2]
                    },
                    {
                        id: 5,
                        relIDs: [5],
                        name: '180x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_05_180x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    },
                    {
                        id: 6,
                        relIDs: [6],
                        name: '180x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_06_180x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    },
                    {
                        id: 7,
                        relIDs: [7],
                        name: '180x05_180x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_07_180x05_180x20.png',
                        wf: 0.1185,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 8,
                        relIDs: [8],
                        name: '180x05',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_08_180x05.png',
                        wf: 0.080,
                        uf: [1.1, 1.1, 1.0, 1.0]
                    },
                    {
                        id: 9,
                        relIDs: [9],
                        name: '180x20_180x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_09_180x20_180x44_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    },
                    {
                        id: 10,
                        relIDs: [10],
                        name: '180x46',
                        image: 'assets/images/frames/aluplast/aluplast_ideal8000_10_180x46.png',
                        wf: 0.070,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [0.0, 0.035, 0.035, 0.035],
                        frsi: [0.0, 0.67, 0.74, 0.74]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [0.0, 0.029, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.76, 0.76]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [0.0, 0.029, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.76, 0.76]
                    }
                ]
            },
            // System 3: IDEAL 7000
            {
                id: 3,
                name: 'IDEAL 7000',
                image: 'assets/images/frames/aluplast/aluplast_ideal7000_02_170x02_170x20.png',
                frameType: FRAME_TYPES.PVC,
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: '170x02',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_01_170x02.png',
                        wf: 0.080,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: '170x02_170x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_02_170x02_170x20.png',
                        wf: 0.1185,
                        uf: [1.2, 1.2, 1.1, 1.1]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: '170x20_170x64_170x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_03_170x20_170x64_170x20.png',
                        wf: 0.142,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: '170x20_170x44_170x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_04_170x20_170x44_170x20.png',
                        wf: 0.182,
                        uf: [1.3, 1.3, 1.3, 1.3]
                    },
                    {
                        id: 5,
                        relIDs: [5],
                        name: '170x20_170x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_05_170x20_170x44_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.3, 1.3]
                    },
                    {
                        id: 6,
                        relIDs: [6],
                        name: '170x20_170x41_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_06_170x20_170x41_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.3, 1.3]
                    },
                    {
                        id: 7,
                        relIDs: [7],
                        name: '170x02_170x20',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_07_170x02_170x20.png',
                        wf: 0.1185,
                        uf: [1.2, 1.2, 1.1, 1.1]
                    },
                    {
                        id: 8,
                        relIDs: [8],
                        name: '170x02',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_08_170x02.png',
                        wf: 0.080,
                        uf: [1.1, 1.1, 1.1, 1.1]
                    },
                    {
                        id: 9,
                        relIDs: [9],
                        name: '170x20_170x44_fest',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_09_170x20_170x44_fest.png',
                        wf: 0.143,
                        uf: [1.3, 1.3, 1.3, 1.3]
                    },
                    {
                        id: 10,
                        relIDs: [10],
                        name: '180x46',
                        image: 'assets/images/frames/aluplast/aluplast_ideal7000_10_180x46.png',
                        wf: 0.070,
                        uf: [1.3, 1.3, 1.2, 1.2]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [0.0, 0.035, 0.035, 0.035],
                        frsi: [0.0, 0.67, 0.74, 0.74]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [0.0, 0.029, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.76, 0.76]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [0.0, 0.029, 0.027, 0.027],
                        frsi: [0.0, 0.69, 0.76, 0.76]
                    }
                ]
            }
        ]
    },
    {
        // ============================================
        // ALUPROF (ID: 9)
        // ============================================
        id: 9,
        name: 'Aluprof',
        image: 'assets/images/frames/aluprof/aluprof.png',
        windowsMap: [
            { wid: 1, f0: [2], f1: [2], f2: [2], f3: [2], f4: [], f5: [], f6: [] },
            { wid: 2, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
            { wid: 3, f0: [1], f1: [1], f2: [1], f3: [2], f4: [2], f5: [2], f6: [5] },
            { wid: 4, f0: [2], f1: [2], f2: [2], f3: [2], f4: [4], f5: [3, 4], f6: [] },
            { wid: 7, f0: [1], f1: [1], f2: [2], f3: [2], f4: [6], f5: [], f6: [] },
            { wid: 8, f0: [1], f1: [1], f2: [2], f3: [6], f4: [2], f5: [3, 4], f6: [] },
            { wid: 9, f0: [1], f1: [1], f2: [1], f3: [], f4: [], f5: [], f6: [] },
            { wid: 13, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
            { wid: 14, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] }
        ],
        systems: [
            // System 1: MB-70 HI
            {
                id: 1,
                name: 'MB-70 HI',
                image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_02_k518101x_k518111x.png',
                frameType: FRAME_TYPES.ALU_METAL,
                variant: '',
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: 'K518101X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_01_k518101x.png',
                        wf: 0.055,
                        uf: [null, 1.7, 1.6, null]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: 'K518101X+K518111X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_02_k518101x_k518111x.png',
                        wf: 0.092,
                        uf: [null, 2.0, 1.9, null]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: 'K518111X+K518113X+K518111X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_03_k518111x_k518113x_k518111x.png',
                        wf: 0.134,
                        uf: [null, 2.0, 1.9, null]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: 'K518111X+K518121X+K518111X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_04_k518111x_k518121x_k518111x.png',
                        wf: 0.150,
                        uf: [null, 2.0, 1.9, null]
                    },
                    {
                        id: 5,
                        relIDs: [5, 6],
                        name: 'K518121X+K518111X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-70-hi_05_k518121x_k518111x.png',
                        wf: 0.113,
                        uf: [null, 1.9, 1.8, null]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [null, 0.043, 0.040, null],
                        frsi: [null, 0.67, 0.74, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [null, 0.034, 0.030, null],
                        frsi: [null, 0.69, 0.76, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [null, 0.034, 0.030, null],
                        frsi: [null, 0.69, 0.76, null]
                    }
                ]
            },
            // System 2: MB-86 SI
            {
                id: 2,
                name: 'MB-86 SI',
                image: 'assets/images/frames/aluprof/aluprof_mb-86 si_02_k518611x_k518702x.png',
                frameType: FRAME_TYPES.ALU_METAL,
                variant: '',
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: 'K518611X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-86 si_01_k518611x.png',
                        wf: 0.0585,
                        uf: [null, 1.5, 1.3, null]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: 'K518611X+K518702X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-86 si_02_k518611x_k518702x.png',
                        wf: 0.103,
                        uf: [null, 1.5, 1.4, null]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: 'K518702X+K518709X+K518702X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-86 si_03_k518702x_k518709x_k518702x.png',
                        wf: 0.156,
                        uf: [null, 1.5, 1.4, null]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: 'K518702X+K518752X+K518702X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-86 si_04_k518702x_k518752x_k518702x.png',
                        wf: 0.1835,
                        uf: [null, 1.4, 1.3, null]
                    },
                    {
                        id: 5,
                        relIDs: [5, 6],
                        name: 'K518752X+K518702X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-86 si_05_k518752x_k518702x.png',
                        wf: 0.139,
                        uf: [null, 1.4, 1.3, null]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [null, 0.041, 0.041, null],
                        frsi: [null, 0.68, 0.75, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [null, 0.032, 0.031, null],
                        frsi: [null, 0.69, 0.77, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [null, 0.032, 0.031, null],
                        frsi: [null, 0.69, 0.77, null]
                    }
                ]
            },
            // System 3: MB-104 Passive SI
            {
                id: 3,
                name: 'MB-104 Passive SI',
                image: 'assets/images/frames/aluprof/aluprof_mb-104si_02_k519011x_k519102x.png',
                frameType: FRAME_TYPES.ALU_METAL,
                variant: '',
                profiles: [
                    {
                        id: 1,
                        relIDs: [1],
                        name: 'K519011X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-104si_01_k519011x.png',
                        wf: 0.0635,
                        uf: [null, null, 1.0, null]
                    },
                    {
                        id: 2,
                        relIDs: [2],
                        name: 'K519011X+K519102X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-104si_02_k519011x_k519102x.png',
                        wf: 0.103,
                        uf: [null, null, 1.1, null]
                    },
                    {
                        id: 3,
                        relIDs: [3],
                        name: 'K519102X+K519109X+K519102X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-104si_03_k519102x_k519109x_k519102x.png',
                        wf: 0.156,
                        uf: [null, null, 1.1, null]
                    },
                    {
                        id: 4,
                        relIDs: [4],
                        name: 'K519102X+K519052X+K519102X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-104si_04_k519102x_k519052x_k519102x.png',
                        wf: 0.1835,
                        uf: [null, null, 1.0, null]
                    },
                    {
                        id: 5,
                        relIDs: [5, 6],
                        name: 'K519052X+K519102X',
                        image: 'assets/images/frames/aluprof/aluprof_mb-104si_05_k519052x_k519102x.png',
                        wf: 0.144,
                        uf: [null, null, 1.0, null]
                    }
                ],
                spacerValues: [
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
                        psi: [null, null, 0.041, null],
                        frsi: [null, null, 0.75, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
                        psi: [null, null, 0.031, null],
                        frsi: [null, null, 0.78, null]
                    },
                    {
                        spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
                        psi: [null, null, 0.031, null],
                        frsi: [null, null, 0.78, null]
                    }
                ]
            }
        ]
    },
     // ============================================
  // GEALAN (ID: 2)
  // ============================================
  {
    id: 2,
    name: 'GEALAN',
    image: 'assets/images/frames/gealan/gealan.png',
    windowsMap: [
      { wid: 1, f0: [2], f1: [2], f2: [2], f3: [2], f4: [], f5: [], f6: [] },
      { wid: 2, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
      { wid: 3, f0: [1], f1: [1], f2: [1], f3: [2], f4: [2], f5: [2], f6: [5] },
      { wid: 4, f0: [2], f1: [2], f2: [2], f3: [2], f4: [4], f5: [3, 4], f6: [] },
      { wid: 6, f0: [8], f1: [8], f2: [8], f3: [7], f4: [7], f5: [7], f6: [9] },
      { wid: 7, f0: [1], f1: [1], f2: [2], f3: [2], f4: [6], f5: [], f6: [] },
      { wid: 8, f0: [1], f1: [1], f2: [2], f3: [6], f4: [2], f5: [3, 4], f6: [] },
      { wid: 9, f0: [1], f1: [1], f2: [1], f3: [], f4: [], f5: [], f6: [] },
      { wid: 10, f0: [1], f1: [1], f2: [1], f3: [10], f4: [], f5: [], f6: [] },
      { wid: 11, f0: [1], f1: [1], f2: [1], f3: [10], f4: [10], f5: [], f6: [] },
      { wid: 12, f0: [1], f1: [1], f2: [1], f3: [1], f4: [10], f5: [10], f6: [10] },
      { wid: 13, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] },
      { wid: 14, f0: [2], f1: [2], f2: [2], f3: [3, 4], f4: [], f5: [], f6: [] }
    ],
    systems: [
      // System 1: S 9000 MD
      {
        id: 1,
        name: 'S 9000 MD',
        image: 'assets/images/frames/gealan/gealan_s9000md_02_6002-6003.png',
        frameType: FRAME_TYPES.PVC,
        profiles: [
          {
            id: 1,
            relIDs: [1],
            name: '6002',
            image: 'assets/images/frames/gealan/gealan_s9000md_01_6002.png',
            wf: 0.070,
            uf: [1.1, 1.1, 1.0, 1.0]
          },
          {
            id: 2,
            relIDs: [2],
            name: '6002-6003',
            image: 'assets/images/frames/gealan/gealan_s9000md_02_6002-6003.png',
            wf: 0.118,
            uf: [1.1, 1.1, 0.92, 0.92]
          },
          {
            id: 3,
            relIDs: [3],
            name: '6003-6012-6003',
            image: 'assets/images/frames/gealan/gealan_s9000md_03_6003-6012-6003.png',
            wf: 0.170,
            uf: [1.1, 1.1, 1.1, 1.1]
          },
          {
            id: 4,
            relIDs: [4],
            name: '6003-6022-6003',
            image: 'assets/images/frames/gealan/gealan_s9000md_04_6003-6022-6003.png',
            wf: 0.204,
            uf: [1.1, 1.1, 1.1, 1.1]
          },
          {
            id: 5,
            relIDs: [5],
            name: '6022-6003',
            image: 'assets/images/frames/gealan/gealan_s9000md_6022_05_6003.png',
            wf: 0.156,
            uf: [1.2, 1.2, 1.2, 1.2]
          },
          {
            id: 6,
            relIDs: [6],
            name: '6003-6022',
            image: 'assets/images/frames/gealan/gealan_s9000md_6003_06_6022.png',
            wf: 0.156,
            uf: [1.2, 1.2, 1.2, 1.2]
          },
          {
            id: 7,
            relIDs: [7],
            name: '6360-6362-Schiebe',
            image: 'assets/images/frames/gealan/gealan_s9000md_07_6360-6362-schiebe.png',
            wf: 0.179,
            uf: [1.2, 1.2, 1.1, 1.1]
          },
          {
            id: 8,
            relIDs: [8],
            name: '6360-6362-Fest',
            image: 'assets/images/frames/gealan/gealan_s9000md_08_6360-6362-fest.png',
            wf: 0.179,
            uf: [1.2, 1.2, 1.2, 1.2]
          },
          {
            id: 9,
            relIDs: [9],
            name: '6362-6362',
            image: 'assets/images/frames/gealan/gealan_s9000md_09_6362-6362.png',
            wf: 0.107,
            uf: [1.6, 1.6, 1.4, 1.4]
          },
          {
            id: 10,
            relIDs: [10],
            name: '6050',
            image: 'assets/images/frames/gealan/gealan_s9000md_10_6050.png',
            wf: 0.092,
            uf: [1.2, 1.2, 1.1, 1.1]
          }
        ],
        spacerValues: [
          {
            spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
            psi: [0.0, 0.034, 0.033, 0.033],
            frsi: [0.0, 0.67, 0.73, 0.73]
          },
          {
            spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
            psi: [0.0, 0.027, 0.026, 0.026],
            frsi: [0.0, 0.68, 0.75, 0.75]
          },
          {
            spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
            psi: [0.0, 0.027, 0.026, 0.026],
            frsi: [0.0, 0.68, 0.75, 0.75]
          }
        ]
      },
      // System 2: S 9000 AD
      {
        id: 2,
        name: 'S 9000 AD',
        image: 'assets/images/frames/gealan/gealan_s9000ad_02_6001-6003.png',
        frameType: FRAME_TYPES.PVC,
        profiles: [
          {
            id: 1,
            relIDs: [1],
            name: '6001',
            image: 'assets/images/frames/gealan/gealan_s9000ad_01_6001.png',
            wf: 0.070,
            uf: [1.1, 1.1, 1.0, 1.0]
          },
          {
            id: 2,
            relIDs: [2],
            name: '6001-6003',
            image: 'assets/images/frames/gealan/gealan_s9000ad_02_6001-6003.png',
            wf: 0.118,
            uf: [1.2, 1.2, 1.0, 1.0]
          },
          {
            id: 3,
            relIDs: [3],
            name: '6003-6012-6003',
            image: 'assets/images/frames/gealan/gealan_s9000ad_03_6003-6012-6003.png',
            wf: 0.170,
            uf: [1.1, 1.1, 1.1, 1.1]
          },
          {
            id: 4,
            relIDs: [4],
            name: '6003-6021-6003',
            image: 'assets/images/frames/gealan/gealan_s9000ad_04_6003-6021-6003.png',
            wf: 0.204,
            uf: [1.3, 1.3, 1.2, 1.2]
          },
          {
            id: 5,
            relIDs: [5],
            name: '6021-6003',
            image: 'assets/images/frames/gealan/gealan_s9000ad_05_6021-6003.png',
            wf: 0.156,
            uf: [1.3, 1.3, 1.2, 1.2]
          },
          {
            id: 6,
            relIDs: [6],
            name: '6003-6021',
            image: 'assets/images/frames/gealan/gealan_s9000ad_06_6003-6021.png',
            wf: 0.156,
            uf: [1.3, 1.3, 1.2, 1.2]
          },
          {
            id: 7,
            relIDs: [7],
            name: '6360-6362-Schiebe',
            image: 'assets/images/frames/gealan/gealan_s9000ad_07_6360-6362-schiebe.png',
            wf: 0.179,
            uf: [1.2, 1.2, 1.1, 1.1]
          },
          {
            id: 8,
            relIDs: [8],
            name: '6360-6362-Fest',
            image: 'assets/images/frames/gealan/gealan_s9000ad_08_6360-6362-fest.png',
            wf: 0.179,
            uf: [1.2, 1.2, 1.2, 1.2]
          },
          {
            id: 9,
            relIDs: [9],
            name: '6362-6362',
            image: 'assets/images/frames/gealan/gealan_s9000ad_09_6362-6362.png',
            wf: 0.107,
            uf: [1.6, 1.6, 1.4, 1.4]
          },
          {
            id: 10,
            relIDs: [10],
            name: '6049',
            image: 'assets/images/frames/gealan/gealan_s9000ad_10_6049.png',
            wf: 0.092,
            uf: [1.2, 1.2, 1.1, 1.1]
          }
        ],
        spacerValues: [
          {
            spacerType: SPACER_TYPES.SWISSPACER_ADVANCE,
            psi: [0.0, 0.034, 0.033, 0.033],
            frsi: [0.0, 0.67, 0.73, 0.73]
          },
          {
            spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE,
            psi: [0.0, 0.027, 0.026, 0.026],
            frsi: [0.0, 0.68, 0.75, 0.75]
          },
          {
            spacerType: SPACER_TYPES.SWISSPACER_ULTIMATE_PRO,
            psi: [0.0, 0.027, 0.026, 0.026],
            frsi: [0.0, 0.68, 0.75, 0.75]
          }
        ]
      }
    ]
  },


    // Add more manufacturers here (Aluprof, Gealan, Veka, etc.)
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get manufacturer by ID
 */
export function getManufacturerById(id: number): SystemManufacturer | undefined {
    return FRAME_MANUFACTURERS.find(m => m.id === id);
}

/**
 * Get profile system by manufacturer ID and system ID
 */
export function getProfileSystemById(
    manufacturerId: number,
    systemId: number
): ProfileSystem | undefined {
    const manufacturer = getManufacturerById(manufacturerId);
    return manufacturer?.systems.find(s => s.id === systemId);
}

/**
 * Get frame profile by relID within a system
 */
export function getFrameProfileByRelId(
    system: ProfileSystem,
    relId: number
): FrameProfile | undefined {
    return system.profiles.find(p => p.relIDs.includes(relId));
}

/**
 * Get window mapping for a manufacturer and window type
 */
export function getWindowMapping(
    manufacturerId: number,
    windowTypeId: number
): ProfileWindowsMapping | undefined {
    const manufacturer = getManufacturerById(manufacturerId);
    return manufacturer?.windowsMap.find(m => m.wid === windowTypeId);
}

/**
 * Get profile for specific frame position based on window type
 */
export function getProfileForPosition(
    manufacturerId: number,
    systemId: number,
    windowTypeId: number,
    framePosition: number // 0-6
): FrameProfile | undefined {
    const system = getProfileSystemById(manufacturerId, systemId);
    const mapping = getWindowMapping(manufacturerId, windowTypeId);

    if (!system || !mapping) return undefined;

    const positionKey = `f${framePosition}` as keyof ProfileWindowsMapping;
    const relIds = mapping[positionKey] as number[];

    if (!relIds || relIds.length === 0) return undefined;

    // Return the first matched profile (typically [2] position)
    return getFrameProfileByRelId(system, relIds[0]);
}
